import * as React from 'react';
import * as Redux from 'redux';
import ReactSVG from 'react-svg';
import { connect } from 'react-redux';
import { translate } from "react-i18next";
import styled from 'styled-components';
import WKT from 'ol/format/wkt';
import OlExtent from 'ol/extent';
import FaultAttributesContainer from './FaultAttributesContainer';
import FaultPhotosContainer from './FaultPhotosContainer';
import FaultCreator from './FaultCreator';
import TextInput from './../../../components/TextInput';
import DefaultButton from './../../../components/DefaultButton';
import Spinner from './../../../components/Spinner';
import IconCancel from './../../../icons/cancel.svg';
import PhotoIcon from './../../../icons/image.svg';
import VideoIcon from './../../../icons/video.svg';
import IconClose from './../../../icons/cancel2.svg';
import BadgeIcon from '../../../components/BadgeIcon';
import {
    selectFault,
    getFaultPhotosAndVideos,
} from './../../../actions/faults';
import {
    centerMap,
    setEditMode,
} from './../../../actions/map';
import GhostButton from '../../../components/GhostButton';

const FaultsManagerWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    overflow: auto;
    align-items: center;
`;

const FilterWrapper = styled.div`
    position: relative;    
    padding: 5px;
    margin: 5px;
    border: 1px solid #bfbfbf;
    border-radius: 4px;
    background: #dddddd;
`;

const FaultsContainer = styled.div`
    overflow: auto;
    display: flex;
    flex-direction: column;
    width: 100%;
    align-items: center;
`;

const FaultDescription = styled.span`
    color:  #2d2d2d;
    font-size: 14px;
    font-weight: 600;
    text-align: left;   
    max-width: 75%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const FaultRow = styled.div`
    width: calc(100% - 10px);
    background-color: #ffffff;
    border-radius: 5px;
    margin: 5px;

    input[type='radio'] {
        display: none;
    }
    label {
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        :hover {
            .rowTitle{
                border: 1px solid #a7c1d7;
                padding: 6.5px;
            }
        }
        .rowTitle {
            cursor: pointer;
            border: 1px solid #ebebeb;
            border-radius: 5px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 6.5px;
            >span{
                font-size: 14px;
                font-weight: 600;
            }
        }
    }
    input[type='radio']:checked+label>.rowTitle {
        border: 1.5px solid ${props => props.borderColor || '#265a9c'};
        box-shadow: 0 1px 3px rgba(0,0,0, 0.3);
        padding: 6px;
    }
`;

const FaultTagLabel = styled.div`
    background-color: ${props => props.color};
    border-radius: 4px;    
    padding: 2px 5px 2px 5px;
    margin-right: 5px;
    margin-bottom: 5px;
    font-size: small;
    font-weight: bold;
    display: flex;
`;

const OtherFaultTagLabel = styled.div`
    background-color: ${props => props.color};
    border-radius: 4px;    
    padding: 2px 5px 2px 5px;
    margin-right: 5px;
    margin-bottom: 5px;
    font-size: small;
    font-weight: bold;
    display: flex;
    :hover {
        box-shadow: inset 0 0 4px #000000;
    }
`;

const DeleteFaultTagButton = styled.div`
    border-radius: 5px;  
    margin-left: 5px;    
    width: 20px;
    display: flex;
    justify-content: center;
    :hover {
        box-shadow: inset 0 0 2px #000000;
    }
`;

const FaultTagsContainer = styled.div`
    display: flex;
    justify-content: unset;
    flex-wrap: wrap;
`;

const OtherFaultTagsContainer = styled.div`
    border: 1px solid #bfbfbf;
    height: 80px;
    border-radius: 4px;
    display: ${props => props.visible ? 'flex' : 'none'};
    flex-wrap: wrap;
    padding: 5px;
    overflow: auto;
    span {
        font-size: small;
        margin-bottom: 2px;
        width: 100%;
    }
`;

class FaultsManager extends React.Component {
    constructor() {
        super();
        this.state = {
            filterTerm: {
                description: '',
                tags: []
            },
            faultCreatorVisible: false,
            faultTagsContainerVisible: false,
        }
    }

    componentDidUpdate(prevProps) {
        const { selectedFault, axiosInstance, getFaultPhotosAndVideos } = this.props;
        if (prevProps.selectedFault !== selectedFault) {
            if (selectedFault) {
                getFaultPhotosAndVideos(axiosInstance, selectedFault.id);
            }
        }
    }

    filterFaults(fault) {
        const { filterTerm } = this.state;
        if (filterTerm.description) {
            if (!fault.description.toUpperCase().includes(filterTerm.description.toUpperCase())) return false;
        }

        if (filterTerm.tags.length > 0) {
            const faultTagsId = fault.faultTags.map(f => f.dicFaultTagId);
            if (!filterTerm.tags.every(tag => faultTagsId.includes(tag))) return false;
        }

        return true;
    }

    addFilterTag(tagId) {
        const { filterTerm } = this.state;
        filterTerm.tags.push(tagId);
        this.setState({
            filterTerm: filterTerm,
            faultTagsContainerVisible: false
        });
    }

    removeFilterTag(tagId) {
        const { filterTerm } = this.state;
        const index = filterTerm.tags.indexOf(tagId);
        if (index !== -1) filterTerm.tags.splice(index, 1);
        this.setState({ filterTerm: filterTerm });
    }

    handleFaultClick(event) {
        const { faults, selectedFault, selectFault } = this.props;
        if (selectedFault && Number(selectedFault.id) === Number(event.target.value)) {
            selectFault(null);
        } else {
            selectFault(faults.find(fault => Number(fault.id) === Number(event.target.value)));
        }
    }

    render(): React.Node {
        const { t, faults, roles, selectedFault, centerMap, faultTagsDictionary } = this.props;
        const { filterTerm, faultCreatorVisible, faultTagsContainerVisible } = this.state;

        return (
            <FaultsManagerWrapper>
                <div style={{
                    width: '100%'
                }}>
                    <FilterWrapper>
                        <TextInput
                            style={{
                                margin: '0px 3px',
                                width: 'calc(100% - 7px)',
                            }}
                            type='text'
                            placeholder={t('Search') + '...'}
                            value={filterTerm.description}
                            onChange={(e) => {
                                filterTerm.description = e.target.value;
                                this.setState({ filterTerm: filterTerm })
                            }}>
                        </TextInput>
                        <DefaultButton
                            style={{
                                position: 'absolute',
                                top: '9px',
                                right: '9px',
                            }}
                            visible={filterTerm.description !== ''}
                            title={t('Clear')}
                            onClick={() => {
                                filterTerm.description = '';
                                this.setState({ filterTerm: filterTerm })
                            }}>
                            <ReactSVG
                                path={IconCancel}
                                style={{
                                    fill: '#ffffff',
                                    width: '20px',
                                    height: '20px'
                                }}
                            />
                        </DefaultButton>
                        <FaultTagsContainer>
                            {filterTerm.tags.map(tagId => {
                                const tag = faultTagsDictionary.find(d => d.id === tagId);
                                return (
                                    <FaultTagLabel
                                        key={tag.id}
                                        color={tag.color}>
                                        {t(tag.name)}
                                        <DeleteFaultTagButton onClick={() => this.removeFilterTag(tag.id)}>
                                            <ReactSVG
                                                path={IconClose}
                                                style={{
                                                    fill: '#000000',
                                                    width: '10px',
                                                    height: '10px',
                                                }}
                                            />
                                        </DeleteFaultTagButton>
                                    </FaultTagLabel>);
                            })}
                            <GhostButton
                                style={{
                                    fontSize: 'small',
                                    fontWeight: 'bold',
                                    marginBottom: '5px'
                                }}
                                visible={!faultTagsContainerVisible}
                                onClick={() => this.setState({ faultTagsContainerVisible: !faultTagsContainerVisible })}>
                                {t('Search by tag')}
                            </GhostButton>
                        </FaultTagsContainer>
                        <OtherFaultTagsContainer visible={faultTagsContainerVisible}>
                            <span>
                                {t('Click on the tag from list below to filter results')}
                            </span>
                            {faultTagsDictionary
                                .filter(tag => {
                                    return !filterTerm.tags.some(tagId => tagId === tag.id);
                                })
                                .map(tag => {
                                    return (
                                        <OtherFaultTagLabel
                                            key={tag.id}
                                            color={tag.color}
                                            onClick={() => this.addFilterTag(tag.id)}>
                                            {t(tag.name)}
                                        </OtherFaultTagLabel>);
                                })}
                        </OtherFaultTagsContainer>
                    </FilterWrapper>
                </div>
                {faults === null
                    ? <Spinner />
                    : <FaultsContainer
                        style={{
                            borderTop: '1px solid #8080801a',
                        }}
                    >
                        {faults
                            .filter(this.filterFaults.bind(this))
                            .map(fault => {
                                const faultSelected = selectedFault
                                    ? Number(fault.id) === Number(selectedFault.id)
                                    : false
                                return (
                                    <FaultRow
                                        key={`${fault.id}_fault_row`}>
                                        <input
                                            readOnly
                                            id={`fault_${String(fault.id)}_radio`}
                                            type="radio"
                                            name="fault"
                                            value={fault.id}
                                            checked={faultSelected}
                                            onClick={this.handleFaultClick.bind(this)} />
                                        <label
                                            htmlFor={`fault_${String(fault.id)}_radio`}
                                            onClick={(e) => {
                                                if (fault.geomWkt) {
                                                    const format = new WKT();
                                                    const feature = format.readFeature(fault.geomWkt)
                                                    const extent = feature.getGeometry().getExtent();
                                                    const center = OlExtent.getCenter(extent);
                                                    centerMap(center);
                                                };
                                            }}>
                                            <div className='rowTitle' style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'unset'
                                            }}>
                                                <FaultDescription>
                                                    {fault.description}
                                                </FaultDescription>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'flex-end',
                                                    marginTop: '6px',
                                                }}>
                                                    <div style={{
                                                        display: 'flex',
                                                        justifyContent: 'unset',
                                                        flexWrap: 'wrap',
                                                        marginRight: faultSelected ? '60px' : '0px'
                                                    }}>
                                                        <div style={{
                                                            display: 'flex'
                                                        }}>
                                                            {fault.videosCount ? <BadgeIcon
                                                                path={VideoIcon}
                                                                count={fault.videosCount} /> : null}
                                                            {fault.photosCount ? <BadgeIcon
                                                                path={PhotoIcon}
                                                                count={fault.photosCount} /> : null}
                                                        </div>
                                                        {fault.faultTags.map(faultTag => {
                                                            const tag = faultTagsDictionary.find(d => d.id === faultTag.dicFaultTagId);
                                                            return (
                                                                <FaultTagLabel
                                                                    key={tag.id}
                                                                    color={tag.color}>
                                                                    {t(tag.name)}
                                                                </FaultTagLabel>);
                                                        })}
                                                    </div>
                                                </div>
                                                {faultSelected ?
                                                    <React.Fragment>
                                                        <FaultAttributesContainer
                                                            openSelf={() => { this.setState({ faultCreatorVisible: true }) }}
                                                        />
                                                        <FaultPhotosContainer editMode={roles !== 'Regular'} />
                                                    </React.Fragment> : null
                                                }
                                            </div>
                                        </label>
                                    </FaultRow>
                                )
                            })}
                        {<DefaultButton
                            style={{
                                position: 'absolute',
                                right: '20px',
                                bottom: '9px',
                                height: '30px',
                                padding: '0 5px',
                            }}
                            disabled={roles === 'Regular'}
                            visible={!faultCreatorVisible}
                            title={t("Add fault")}
                            onClick={() => {
                                this.setState({
                                    faultCreatorVisible: true,
                                });
                                this.props.setEditMode(true);
                                this.props.selectFault(null);
                            }}>
                            {t('Add fault')}
                        </DefaultButton>}
                        <FaultCreator
                            visible={faultCreatorVisible}
                            closeSelf={() => {
                                this.setState({ faultCreatorVisible: false });
                            }}
                        />
                    </FaultsContainer>
                }
            </FaultsManagerWrapper >
        );
    }
}

const mapStateToProps = state => ({
    axiosInstance: state.auth.get('axiosInstance'),
    selectedFault: state.faults.get('selectedFault'),
    faults: state.faults.get('faults'),
    faultTagsDictionary: state.faults.get('faultTagsDictionary'),
    roles: state.auth.get('roles'),
});

const mapDispatchToProps = dispatch => Redux.bindActionCreators({
    getFaultPhotosAndVideos,
    selectFault,
    centerMap,
    setEditMode,
}, dispatch);

export default translate("translations")(connect(mapStateToProps, mapDispatchToProps)(FaultsManager));


// WEBPACK FOOTER //
// ./src/containers/content/faultsManager/FaultsManager.jsx